import { call, put, select } from 'redux-saga/effects';
import * as R from 'remeda';
import {
  aggregatedActivityActions,
  aggregatedActivitySelectors,
} from 'store/aggregated/aggregatedActivity';
import { sagaWrapper } from 'store/helpers';
import { ActionType } from 'typesafe-actions';
import { arrayUtility } from 'utility';
import { mutateSet } from 'utility/domain';

import * as productivityActions from './actions';

/**
 * Toggle the "off-hours" overlay for a single (date × entity) cell.
 *
 * Finds the aggregatedActivity rows that sit at the intersection of the
 * clicked date and entity (zone/activity/sub-zone), and updates each row's
 * `modifiedSpentTime` — the what-if value the pivot table renders on top of
 * the real spentTime. The reducer in store/aggregated/aggregatedActivity then
 * recomputes the dependent views.
 *
 * payload.isOff === true  → restore modifiedSpentTime to spentTime
 * payload.isOff === false → zero modifiedSpentTime out
 */
export function* startToggleHoursSaga(
  action: ActionType<typeof productivityActions.startToggleHours>,
) {
  function* saga() {
    const { dateId, entityId, isOff } = action.payload;

    // Activity-hour ids for the clicked date.
    const byDate: ReturnType<typeof aggregatedActivitySelectors.byDate> =
      yield select(aggregatedActivitySelectors.byDate);
    const dateIds = byDate[dateId];
    if (!R.isDefined(dateIds)) return;

    // Activity-hour ids for the clicked entity (zone / activity / sub-zone).
    const byEntity: ReturnType<typeof aggregatedActivitySelectors.byEntity> =
      yield select(aggregatedActivitySelectors.byEntity);
    const entityIds = byEntity[entityId];
    if (!R.isDefined(entityIds)) return;

    // The rows that belong to both buckets are the ones to toggle.
    const intersectingIds = arrayUtility.intersectingIds(dateIds, entityIds);

    const domain: ReturnType<typeof aggregatedActivitySelectors.domain> =
      yield select(aggregatedActivitySelectors.domain);

    intersectingIds.forEach((id) => {
      const item = domain.byId[id];
      // Shouldn't happen — the id came from the same domain we just selected.
      if (!R.isDefined(item)) throw 'will not happen';

      if (isOff) item.modifiedSpentTime = item.spentTime;
      else item.modifiedSpentTime = 0;

      mutateSet(domain, item);
    });

    yield put(aggregatedActivityActions.setDomain(domain));
  }

  yield call(sagaWrapper, { saga, type: action.type });
}
